import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { billOfQuantitiesApi, BillOfQuantitiesRequest, BillOfQuantitiesResponse } from '../api/billOfQuantities';
import { getScopedLatestVersionNumber, type BoqVersionRoutePrefix } from '../utils/boqVersionPrefix';

export const BOQ_QUERY_KEYS = {
  all: ['billOfQuantities'] as const,
  list: (params?: BillOfQuantitiesRequest) => ['billOfQuantities', 'list', params] as const,
  detail: (id: string) => ['billOfQuantities', 'detail', id] as const,
  byProject: (projectName?: string) => ['billOfQuantities', 'byProject', projectName] as const,
  versionHistory: (projectName?: string) => ['boq-versions', projectName] as const,
  boqVersion: (projectName?: string, version?: number | string) => ['boq-version', projectName, version] as const,
};

export const useBillOfQuantities = (params?: BillOfQuantitiesRequest, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: BOQ_QUERY_KEYS.list(params),
    queryFn: () => billOfQuantitiesApi.get(params),
    enabled: options?.enabled !== false, // Enable by default unless explicitly disabled
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useBillOfQuantitiesByProject = (
  projectName: string | undefined,
  options?: { enabled?: boolean; oppNo?: string },
) => {
  return useQuery({
    queryKey: BOQ_QUERY_KEYS.byProject(projectName),
    queryFn: async () => {
      const result = await billOfQuantitiesApi.getByProject(projectName!, options?.oppNo);
      // API returns a single object; wrap in array to match the format expected by callers
      return result ? [result] : [];
    },
    enabled: !!projectName && options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateBillOfQuantities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: BillOfQuantitiesRequest | { data: BillOfQuantitiesRequest; statusChanged?: boolean }) => {
      // Support both old format (direct data) and new format (with statusChanged flag)
      let data: BillOfQuantitiesRequest;
      let statusChanged = false;

      // Check if it's the new format with 'data' property
      if (params && typeof params === 'object' && 'data' in params && 'statusChanged' in params) {
        data = params.data;
        statusChanged = params.statusChanged ?? false;
      } else {
        // Old format - just the data directly (don't create version)
        data = params as BillOfQuantitiesRequest;
        statusChanged = false;
      }

      const opportunityNo = (data as any).opportunityNo as string | undefined;

      if (statusChanged) {
        // Status changed - call both endpoints simultaneously
        console.log('📤 Status changed - Submitting to both /billofquantities and /boq-versions');

        // Ensure "Pending" status is converted to "In-progress"
        const normalizedStatus = (data.boqStatus === "Pending" || data.status === "Pending")
          ? "In-progress"
          : (data.boqStatus || data.status || 'In-progress');

        const projectName = data.project?.name || 'Unnamed Project';

        // Prepare version payload with required structure
        const versionPayload = {
          projectName,
          boq: data, // Entire BOQ payload goes inside "boq" field
          changeLog: `BOQ Status changed to ${normalizedStatus}`,
          createRateCardVersion: false,
        };

        console.log('📦 BOQ version payload structure:', {
          projectName: versionPayload.projectName,
          hasBoq: !!versionPayload.boq,
          changeLog: versionPayload.changeLog,
          createRateCardVersion: versionPayload.createRateCardVersion,
        });

        // Determine if a version already exists to decide POST vs PUT
        let versionExists = false;
        let existingVersion = 1;
        try {
          const versionHistory = await billOfQuantitiesApi.getVersionHistory(projectName, opportunityNo);
          const versions = versionHistory.versions;
          const totalVersions = versionHistory.totalVersions;

          const scopedLatest = getScopedLatestVersionNumber(versions);
          if (totalVersions > 0 && Array.isArray(versions) && versions.length > 0 && scopedLatest !== undefined) {
            existingVersion = scopedLatest;
            versionExists = true;
            console.log(`📌 Version ${existingVersion} exists - will use PUT to update`);
          } else {
            console.log('📌 No versions exist - will use POST to create');
          }
        } catch {
          console.log('📌 Could not fetch version history - will use POST to create');
        }

        const versionCall = versionExists
          ? billOfQuantitiesApi.updateVersion(projectName, existingVersion, versionPayload as any, opportunityNo)
          : billOfQuantitiesApi.createVersion(versionPayload as any, opportunityNo);

        const [boqResponse, versionResponse] = await Promise.all([
          billOfQuantitiesApi.create(data, opportunityNo),
          versionCall,
        ]);

        console.log('✅ BOQ submission response:', boqResponse);
        console.log('✅ BOQ version response:', versionResponse);

        // Return the main BOQ response
        return boqResponse;
      } else {
        // No status change - only call main BOQ endpoint
        console.log('📤 No status change - Submitting to /billofquantities only');
        const boqResponse = await billOfQuantitiesApi.create(data, opportunityNo);
        console.log('✅ BOQ submission response:', boqResponse);
        return boqResponse;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
    },
    onError: (error: any) => {
      console.error('❌ Error submitting BOQ:', error);
      // Even if one fails, log which one failed
      if (error.config?.url?.includes('boq-versions')) {
        console.error('❌ Failed to create BOQ version');
      } else {
        console.error('❌ Failed to create main BOQ');
      }
    },
  });
};

export const useUpdateBillOfQuantities = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillOfQuantitiesRequest }) => 
      billOfQuantitiesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
    },
  });
};

export const useDeleteBillOfQuantities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billOfQuantitiesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });
    },
  });
};

/**
 * Hook for updating BOQ versions
 * Automatically wraps the payload in the correct structure for version updates
 */
export const useUpdateBoqVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectName,
      version,
      boqPayload,
      changeLog = 'Auto-saved changes',
      oppNo,
    }: {
      projectName: string;
      version: number;
      boqPayload: BillOfQuantitiesRequest;
      changeLog?: string;
      oppNo?: string;
    }) => {
      console.log(`📤 Updating BOQ version: ${projectName} v${version}`);

      // Wrap in version payload structure
      const versionPayload = {
        projectName: projectName,
        boq: boqPayload, // Entire BOQ payload goes inside "boq" field
        changeLog: changeLog,
        version: version, // Include version number in payload
      };

      const response = await billOfQuantitiesApi.updateVersion(
        projectName,
        version,
        versionPayload as any,
        oppNo,
      );

      console.log('✅ BOQ version updated successfully');
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate queries for this specific project
      queryClient.invalidateQueries({
        queryKey: ['boq-versions', variables.projectName],
      });
    },
    onError: (error: any) => {
      console.error('❌ Error updating BOQ version:', error);
    },
  });
};

export const useBoqVersionHistory = (
  projectName: string | undefined,
  options?: { enabled?: boolean; oppNo?: string }
) => {
  return useQuery({
    queryKey: [...BOQ_QUERY_KEYS.versionHistory(projectName), options?.oppNo],
    queryFn: () => billOfQuantitiesApi.getVersionHistory(projectName!, options?.oppNo),
    enabled: !!projectName && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Only refetch this on mount / when it goes stale on its own 30s timer —
    // not merely because the browser tab regained focus. `staleTime: 30s` is
    // already short enough to pick up a save made elsewhere; refetching on
    // every alt-tab back to this page as well (TanStack Query's default)
    // was the cause of /groundworks/review's "reloads every time you switch
    // tabs" symptom, since this hook is called from GroundworksReview.tsx,
    // SumAnalysis.tsx, and PlotMatrix.tsx all at once.
    refetchOnWindowFocus: false,
  });
};

export const useBoqLatestVersion = (
  projectName: string | undefined,
  options?: {
    enabled?: boolean;
    oppNo?: string;
    // Pages nested under /roads-sewers or /groundworks leave this undefined
    // and get route-based prefix detection (both for scoping which sequence
    // "latest" means, and for the version PUT/GET's own GWKS-N/RS-N key).
    // Callers that render outside both routes — this hook's own MainLayout
    // caller is the main one, since it's mounted on every page — must pass
    // this explicitly, or the resolved "latest version" comes back as a bare
    // number instead of GWKS-N/RS-N, and the GET below is sent that way too.
    prefixOverride?: BoqVersionRoutePrefix | null;
  }
) => {
  const { data: versionHistoryData } = useBoqVersionHistory(projectName, options);

  const latestVersionNum = useMemo(
    () => getScopedLatestVersionNumber(versionHistoryData?.versions, options?.prefixOverride),
    [versionHistoryData, options?.prefixOverride],
  );

  return useQuery({
    queryKey: [...BOQ_QUERY_KEYS.boqVersion(projectName, latestVersionNum), options?.oppNo, options?.prefixOverride],
    queryFn: () => billOfQuantitiesApi.getBoqVersion(projectName!, latestVersionNum!, options?.oppNo, options?.prefixOverride),
    enabled: !!projectName && latestVersionNum !== undefined && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // See useBoqVersionHistory's own comment above — don't refetch just
    // because the tab regained focus.
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches the specific BOQ version stored in localStorage as "currentBoqVersion".
 * Use this instead of useBoqLatestVersion when viewing a historical tender snapshot.
 *
 * Mirrors useBoqLatestVersion: checks version history first. When there is only one
 * version the API returns versions: [] — selectedVersionNum stays undefined and the
 * specific-version fetch is skipped, matching the behaviour of useBoqLatestVersion
 * in edit/review mode.
 */
export const useBoqSelectedVersion = (
  projectName: string | undefined,
  // See useBoqLatestVersion's own `prefixOverride` doc comment above — same
  // reasoning applies here for callers outside /groundworks and /roads-sewers.
  options?: { enabled?: boolean; oppNo?: string; prefixOverride?: BoqVersionRoutePrefix | null }
) => {
  const { data: versionHistoryData } = useBoqVersionHistory(projectName, options);

  const selectedVersionNum = useMemo(() => {
    if (!versionHistoryData?.versions?.length) return undefined;
    try {
      const raw = localStorage.getItem("currentBoqVersion");
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { version?: number | string } | null;
      return parsed?.version;
    } catch {
      return undefined;
    }
  }, [versionHistoryData]);

  return useQuery({
    queryKey: [...BOQ_QUERY_KEYS.boqVersion(projectName, selectedVersionNum), options?.oppNo, options?.prefixOverride],
    queryFn: () => billOfQuantitiesApi.getBoqVersion(projectName!, selectedVersionNum!, options?.oppNo, options?.prefixOverride),
    enabled: !!projectName && selectedVersionNum !== undefined && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // See useBoqVersionHistory's own comment above — don't refetch just
    // because the tab regained focus.
    refetchOnWindowFocus: false,
  });
};